import axios from "axios";
import { unset } from "lodash";
import * as types from "./types";
import * as accountTypes from '@/modules/account/store/types'
import { reportLockFromPendencies, toPendingCount } from '@/modules/account/store/report_lock'
import { FORM_VERSION, REPORT_ERROR_CODES, buildFinalizePayload } from '@/constants/appointment_report_v2'
import { IDEMPOTENCY_HEADER, newIdempotencyKey } from '@/helpers/idempotency'
import { hasApiErrorCode } from '@/helpers/error_messages'

// O guard de rotas aguarda esta consulta antes de abrir qualquer tela do PN: sem timeout, um servidor
// lento travaria a navegação. Estourou → a trava fica como estava e o servidor segue aplicando o 423.
const REPORT_PENDENCIES_TIMEOUT_MS = 10000

// Depois de um report gravado a trava pode ter mudado: reconsulta em segundo plano (nunca falha o report).
const refreshReportLockInBackground = dispatch => {
  dispatch(accountTypes.REFRESH_REPORT_LOCK, { force: true }).catch(() => {})
}

// Finalização de QUALQUER tipo (`POST appointments/{id}/report/finalize`): o Momento X e os tipos padrão usam a mesma
// rota, o mesmo cabeçalho `Idempotency-Key` e a mesma resposta `{ report, replayed, pending_count }` — só o corpo muda
// (quem escolhe o formulário é o servidor, pelo tipo do compromisso). A atualização da trava fica aqui, uma vez só,
// para as duas actions nunca divergirem no que liberam.
const finalizeAppointmentReport = ({ commit, dispatch, rootState }, id, body, idempotencyKey) => {
  const headers = { [IDEMPOTENCY_HEADER]: idempotencyKey || newIdempotencyKey() }
  return axios.post(`/api/appointments/${id}/report/finalize`, body, { headers })
    .then(resp => {
      if (!resp) return Promise.reject(new Error('Resposta vazia ao finalizar o report.'))
      const lock = rootState.account && rootState.account.reportLock
      const pendingCount = toPendingCount(resp.data && resp.data.pending_count, null)
      if (lock && pendingCount !== null) {
        // Finalizar só pode liberar: a lista sem este compromisso, com a contagem do servidor.
        commit(accountTypes.MUTATE_REPORT_LOCK, {
          locked: lock.locked && pendingCount > 0,
          pendingIds: lock.pendingIds.filter(pendingId => pendingId !== Number(id)),
          count: pendingCount,
          checkedAt: Date.now(),
        })
      }
      refreshReportLockInBackground(dispatch)
      return resp
    })
    .catch(err => {
      if (hasApiErrorCode(err, REPORT_ERROR_CODES.ALREADY_REPORTED)) refreshReportLockInBackground(dispatch)
      return Promise.reject(err)
    })
}

export default {
  [types.SEARCH_APPOINTMENTS]: ({ commit }, payload) => {
    const resource = `/api/appointments/search?page=${payload.currentPage}`;
    delete payload.currentPage;
    axios
      .post("/api/appointments/search/totalizers", payload)
      .then((resp) => {
        commit(types.MUTATE_APPOINTMENTS_SEARCH_TOTALIZERS, resp.data);
        return Promise.resolve(resp);
      })
      .catch(() => {
        // Sem .catch os totalizadores viravam rejeição não tratada; ao falhar,
        // zeramos os cards para não exibir números órfãos.
        commit(types.MUTATE_APPOINTMENTS_SEARCH_TOTALIZERS, undefined);
      });
    return axios
      .post(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_APPOINTMENTS_SEARCH_RESULT, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        // Se a listagem falha, zera os totalizadores para não deixar cards
        // preenchidos com a tabela vazia (sintoma reportado).
        commit(types.MUTATE_APPOINTMENTS_SEARCH_TOTALIZERS, undefined);
        return Promise.reject(err);
      });
  },
  [types.GET_APPOINTMENT]: ({}, payload) => {
    return axios
      .get(`/api/appointments/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.STORE_APPOINTMENT]: ({}, payload) => {
    let save = axios.post;
    let route = "/api/appointments";
    if (payload.id) {
      save = axios.put;
      route = `/api/appointments/${payload.id}`;
    } else if (payload.is_multiple) {
      route = "/api/appointments/multiples";
    }
    unset(payload.id);
    unset(payload.is_multiple);
    return save(route, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.DELETE_APPOINTMENT]: ({}, payload) => {
    return axios
      .delete(`/api/appointments/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_APPOINTMENT_EVENT_TYPES]: ({ commit }, payload) => {
    return axios
      .get(`/api/appointments/appointment_types`)
      .then((resp) => {
        commit(types.MUTATE_APPOINTMENT_EVENT_TYPES, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_APPOINTMENT_MEETING_FORMAT_TYPES]: ({ commit }, payload) => {
    return axios
      .get(`/api/appointments/meeting_format_types`)
      .then((resp) => {
        commit(types.MUTATE_APPOINTMENT_MEETING_FORMAT_TYPES, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.OPEN_NEW_APPOINTMENT_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_NEW_APPOINTMENT_SIDEBAR, { ...payload, visible: true });
  },
  [types.OPEN_APPOINTMENT_REPORT_DETAILS_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_APPOINTMENT_REPORT_DETAILS_SIDEBAR, {
      ...payload,
      visible: true,
    });
  },
  [types.GET_APPOINTMENT_CUSTOMERS]: ({ commit }, payload) => {
    return axios
      .get("/api/customers_info/summary/appointment", { params: payload })
      .then((resp) => {
        commit(types.MUTATE_APPOINTMENT_CUSTOMERS, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.EXPORT_APPOINTMENTS]: ({ commit }, payload) => {
    return axios
      .post("/api/appointments/export", payload, { responseType: "blob" })
      .then((resp) => {
        let fileURL = window.URL.createObjectURL(new Blob([resp.data]));
        let fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute("download", "compromissos.xlsx");
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GENERATE_NPS_LINK]: ({}, appointmentId) => {
    return axios
      .get(`/api/nps_surveys/generate_link/${appointmentId}`)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.CHECK_FOR_OVERLAPPING_APPOINTMENTS]: ({}, payload) => {
    return axios
      .post(`/api/appointments/overlapping_appointments_check`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_CONSULTANTS_SPECIALIST]: ({ commit }, payload) => {
    return axios
      .get(`/api/interviews/proposal/consultants`)
      .then((resp) => {
        commit(types.MUTATE_CONSULTANTS_SPECIALIST, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_APPOINTMENT_QUALITATIVE_HIGHLIGHTS]: ({}, payload) => {
    return axios
      .get(`/api/appointments/qualitative_highlights/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  // ---- Report de Momento X (fluxo CX) + central de pendências ----

  // Lista completa das pendências do PN (mais antiga primeiro): `{ data: [{ id, event_type, appointment_start,
  // appointment_end, status_id, customer_id, customer_name, days_overdue, has_draft }], count, blocked,
  // server_now }`. Também atualiza a trava (`account/reportLock`) — a central e o guard leem o mesmo estado.
  // Uma resposta mais antiga que o último estado gravado (ex.: 423 ou finalização no meio) é ignorada.
  [types.GET_REPORT_PENDENCIES]: ({ commit, rootState }) => {
    const requestedAt = Date.now()
    return axios.get('/api/users/report_pendencies', { timeout: REPORT_PENDENCIES_TIMEOUT_MS })
      .then(resp => {
        if (!resp) return Promise.reject(new Error('Resposta vazia ao consultar as pendências de report.'))
        const current = rootState.account && rootState.account.reportLock
        if (!current || !current.checkedAt || requestedAt >= current.checkedAt) {
          commit(accountTypes.MUTATE_REPORT_LOCK, reportLockFromPendencies(resp.data))
        }
        return resp
      })
  },
  // `{ draft: { answers, form_version, version, updated_at } | null, appointment, report_opens_at, server_now,
  // report: {...} | null }` — `report` preenchido = compromisso já reportado (mostrar a confirmação).
  [types.GET_REPORT_DRAFT]: (_, appointmentId) => axios.get(`/api/appointments/${appointmentId}/report/draft`),
  // payload `{ id, answers, version, formVersion? }` (`version` = versão do rascunho que a tela tem; null na criação;
  // `formVersion` = 1 no formulário dos tipos padrão, 2 — o padrão — no Momento X do CX).
  // 409 APPOINTMENT_REPORT_DRAFT_CONFLICT traz o rascunho atual em `response.data.draft`; 423
  // APPOINTMENT_REPORT_NOT_OPEN = antes do horário.
  [types.SAVE_REPORT_DRAFT]: (_, payload) => {
    const {
      id,
      answers,
      version,
      formVersion,
    } = payload
    return axios.put(`/api/appointments/${id}/report/draft`, {
      answers: answers || {},
      form_version: formVersion || FORM_VERSION,
      version: version === undefined ? null : version,
    })
  },
  [types.DISCARD_REPORT_DRAFT]: (_, appointmentId) => axios.delete(`/api/appointments/${appointmentId}/report/draft`),
  // payload `{ id, answers, idempotencyKey }`. Envia só o caminho ativo das respostas (`buildFinalizePayload`).
  // `idempotencyKey`: gere uma com `newIdempotencyKey()` por intenção de finalizar e REPITA a mesma em
  // "Tentar novamente" — o servidor devolve o report já gravado (`replayed: true`) em vez de duplicar.
  // Sucesso: `{ report, replayed, pending_count }`. Erros tratados pela tela: 409 APPOINTMENT_ALREADY_REPORTED,
  // 422 (validação / NOT_OWNER / NOT_REPORTABLE), 423 APPOINTMENT_REPORT_NOT_OPEN.
  [types.FINALIZE_MX_REPORT]: (context, payload) => {
    const { id, answers, idempotencyKey } = payload
    return finalizeAppointmentReport(context, id, buildFinalizePayload(answers), idempotencyKey)
  },
  // Report dos demais tipos — Entrevista, Parceria, Negociação e recrutamento de PN — na MESMA rota do Momento X (as
  // perguntas de sempre, `report_version = 1`). payload `{ id, payload, idempotencyKey }`: `payload` já é o corpo do
  // contrato (`buildStandardFinalizePayload` de constants/appointment_report_standard — só o caminho ativo, em JSON
  // tipado). Mesma chave em toda tentativa do mesmo rascunho; mesmos erros do Momento X (409 ALREADY_REPORTED, 422
  // validação / NOT_OWNER / NOT_REPORTABLE, 423 NOT_OPEN). O reagendamento continua sendo o STORE_APPOINTMENT à parte.
  [types.FINALIZE_STANDARD_REPORT]: (context, { id, payload, idempotencyKey }) => finalizeAppointmentReport(
    context,
    id,
    payload || {},
    idempotencyKey,
  ),

  // ---- Página cheia de Novo compromisso ----

  // Compromissos de UM dia do PN, pela busca da agenda (`POST /api/appointments/search`), SEM tocar no estado
  // da lista de Compromissos (SEARCH_APPOINTMENTS grava resultado e totalizadores). payload
  // `{ date: 'YYYY-MM-DD', consultantId, customerId?, eventType? }`; resolve com a lista (`[]` se vier vazia).
  // Usos: achar o id do compromisso recém-criado (`POST /api/appointments` só devolve a mensagem) e mostrar
  // com qual compromisso o horário conflita.
  [types.SEARCH_DAY_APPOINTMENTS]: (_, payload) => {
    const {
      date,
      consultantId,
      customerId,
      eventType,
    } = payload
    const body = { limit: 100, appointment_start: date }
    if (consultantId) body.consultant_id = [consultantId]
    if (customerId) body.customer_id = customerId
    if (eventType) body.event_type = [eventType]
    return axios.post('/api/appointments/search?page=1', body)
      .then(resp => {
        if (!resp) return Promise.reject(new Error('Resposta vazia ao buscar os compromissos do dia.'))
        const page = resp.data || {}
        return Array.isArray(page.data) ? page.data : []
      })
  },
};
